import {ChartQuery, ChartSettings} from 'entities/chart';
import {ChartType, EventFilter} from '@/views/storyboard/utils/chartConstants';
import {chartQueryDefaultProps} from '@/views/storyboard/utils/chartPropTypes';
import {resetAggsItemForOutput} from '@/views/storyboard/utils/applyChartTypeChange';
import {
    cleanupMetricQueryFields,
    cleanupTypeSpecificQueryFields,
    resolveOutputForChartType,
    sanitizeChartQueryForRequest,
} from '@/views/storyboard/utils/sanitizeChartQuery';

export type SharedChartQuery = Pick<
    ChartQuery,
    'query_string' | 'fields_to_query' | 'additionalFilters' | 'all_fields' | 'additionalData'
>;

export type PerTypeChartQuery = Pick<
    ChartQuery,
    | 'output'
    | 'aggs_item'
    | 'aggs_item_list'
    | 'intervalUnit'
    | 'intervalNumber'
    | 'metric_timeline_type'
    | 'metric_aggregations_type'
    | 'metric_aggregations_aggs_item'
    | 'pivot_col_aggs_item'
    | 'metric_interval'
    | 'concordance_query'
    | 'concordance_width'
    | 'concordance_lines'
    | 'n_gram'
    | 'filtering_lists'
    | 'trending_or_disappearing'
    | 'trending_comparable_period'
    | 'trending_period'
    | 'nested_terms_include_missing'
    | 'wordcloud_most_common_words'
    | 'wordcloud_text_start_point'
    | 'wordcloud_text_end_point'
>;

export type SharedChartSettings = Pick<
    ChartSettings,
    'title' | 'description' | 'width' | 'height' | 'id' | 'storyboardId'
>;

export type PerTypeChartSettings = Pick<
    ChartSettings,
    | 'linearRegression'
    | 'comparablePeriod'
    | 'movingAverageDays'
    | 'movingAverageDaysCenter'
    | 'scalingFactor'
    | 'barOrientation'
    | 'barSortOrder'
    | 'dataLabelPosition'
>;

export interface CachedChartTypeState {
    query: PerTypeChartQuery;
    settings: PerTypeChartSettings;
}

export function extractSharedChartQuery(chartQuery: ChartQuery): SharedChartQuery {
    return {
        query_string: chartQuery.query_string,
        fields_to_query: chartQuery.fields_to_query,
        additionalFilters: chartQuery.additionalFilters,
        all_fields: chartQuery.all_fields,
        additionalData: chartQuery.additionalData,
    };
}

export function extractSharedChartSettings(chartSettings: ChartSettings): SharedChartSettings {
    return {
        title: chartSettings.title,
        description: chartSettings.description,
        width: chartSettings.width,
        height: chartSettings.height,
        id: chartSettings.id,
        storyboardId: chartSettings.storyboardId,
    };
}

export function extractPerTypeChartQuery(chartQuery: ChartQuery): PerTypeChartQuery {
    return clonePerTypeChartQuery({
        output: chartQuery.output,
        aggs_item: chartQuery.aggs_item,
        aggs_item_list: chartQuery.aggs_item_list ?? [],
        intervalUnit: chartQuery.intervalUnit,
        intervalNumber: chartQuery.intervalNumber,
        metric_timeline_type: chartQuery.metric_timeline_type,
        metric_aggregations_type: chartQuery.metric_aggregations_type,
        metric_aggregations_aggs_item: chartQuery.metric_aggregations_aggs_item,
        pivot_col_aggs_item: chartQuery.pivot_col_aggs_item,
        metric_interval: chartQuery.metric_interval,
        concordance_query: chartQuery.concordance_query,
        concordance_width: chartQuery.concordance_width,
        concordance_lines: chartQuery.concordance_lines,
        n_gram: chartQuery.n_gram,
        filtering_lists: chartQuery.filtering_lists ?? [],
        trending_or_disappearing: chartQuery.trending_or_disappearing,
        trending_comparable_period: chartQuery.trending_comparable_period,
        trending_period: chartQuery.trending_period,
        nested_terms_include_missing: chartQuery.nested_terms_include_missing,
        wordcloud_most_common_words: chartQuery.wordcloud_most_common_words,
        wordcloud_text_start_point: chartQuery.wordcloud_text_start_point,
        wordcloud_text_end_point: chartQuery.wordcloud_text_end_point,
    });
}

export function extractPerTypeChartSettings(chartSettings: ChartSettings): PerTypeChartSettings {
    return {
        linearRegression: chartSettings.linearRegression,
        comparablePeriod: chartSettings.comparablePeriod,
        movingAverageDays: chartSettings.movingAverageDays,
        movingAverageDaysCenter: chartSettings.movingAverageDaysCenter,
        scalingFactor: chartSettings.scalingFactor,
        barOrientation: chartSettings.barOrientation,
        barSortOrder: chartSettings.barSortOrder,
        dataLabelPosition: chartSettings.dataLabelPosition,
    };
}

function clonePerTypeChartQuery(query: PerTypeChartQuery): PerTypeChartQuery {
    return {
        ...query,
        aggs_item_list: query.aggs_item_list.map((item) => ({...item})),
        filtering_lists: query.filtering_lists.map((item) => ({...item})),
    };
}

function clonePerTypeChartSettings(settings: PerTypeChartSettings): PerTypeChartSettings {
    return {...settings};
}

export function buildChartQueryFromParts(
    shared: SharedChartQuery,
    perType: PerTypeChartQuery,
    chartQueryId?: number,
): ChartQuery {
    const query: ChartQuery = {
        ...chartQueryDefaultProps,
        ...shared,
        ...clonePerTypeChartQuery(perType),
    };

    if (chartQueryId !== undefined) {
        query.id = chartQueryId;
    }

    return query;
}

export function buildChartSettingsFromParts(
    shared: SharedChartSettings,
    perType: PerTypeChartSettings,
    chartType: ChartType,
): ChartSettings {
    return {
        ...shared,
        ...clonePerTypeChartSettings(perType),
        type: chartType,
    };
}

export function restoreChartTypeState(
    nextType: ChartType,
    sharedQuery: SharedChartQuery,
    sharedSettings: SharedChartSettings,
    cached: CachedChartTypeState,
    eventFilters: EventFilter[] = [],
): {chartQuery: ChartQuery; chartSettings: ChartSettings} {
    let chartQuery = buildChartQueryFromParts(sharedQuery, cached.query, sharedSettings.id);
    chartQuery.output = resolveOutputForChartType(nextType, chartQuery.output);
    chartQuery = cleanupTypeSpecificQueryFields(chartQuery, nextType);
    chartQuery = cleanupMetricQueryFields(chartQuery, nextType);
    chartQuery.aggs_item = resetAggsItemForOutput(
        chartQuery.output,
        chartQuery.aggs_item,
        eventFilters,
    );
    chartQuery = sanitizeChartQueryForRequest(chartQuery, nextType);

    const chartSettings = buildChartSettingsFromParts(
        sharedSettings,
        cached.settings,
        nextType,
    );

    return {chartQuery, chartSettings};
}

export class ChartTypeSettingsCache {
    private entries = new Map<ChartType, CachedChartTypeState>();

    seed(chartType: ChartType, chartQuery: ChartQuery, chartSettings: ChartSettings): void {
        if (this.entries.has(chartType)) {
            return;
        }

        this.save(chartType, chartQuery, chartSettings);
    }

    save(chartType: ChartType, chartQuery: ChartQuery, chartSettings: ChartSettings): void {
        this.entries.set(chartType, {
            query: extractPerTypeChartQuery(chartQuery),
            settings: extractPerTypeChartSettings(chartSettings),
        });
    }

    has(chartType: ChartType): boolean {
        return this.entries.has(chartType);
    }

    get(chartType: ChartType): CachedChartTypeState | undefined {
        const entry = this.entries.get(chartType);
        if (!entry) {
            return undefined;
        }

        return {
            query: clonePerTypeChartQuery(entry.query),
            settings: clonePerTypeChartSettings(entry.settings),
        };
    }
}
